import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import Blade from "./Blade";
import { IGroupedDevices } from "common/store/DeviceStore";
import theme from "common/theme/theme";
import useProjectStore, { IDeviceInstance, IObstructionInstance } from "common/store/ProjectStore";
import useDeviceStore from "common/store/DeviceStore";
import DeviceCategory from "common/constants/DeviceCategory";
import { LanguageContext } from "common/contexts/LanguageContext";
import useLookupsStore from "common/store/LookupsStore";

interface IDeviceInfo {
  count: number;
  groupedDevice: IGroupedDevices;
}

const SelectedDevicesBlade: FC = () => {
  const [gatewayDevices, setGatewayDevices] = useState<IGroupedDevices[]>([]);
  const [instrumentDevices, setInstrumentDevices] = useState<IGroupedDevices[]>([]);
  const [otherDevices, setOtherDevices] = useState<IGroupedDevices[]>([]);
  const [deviceMap, setDeviceMap] = useState<Record<number, IGroupedDevices>>({});
  const { activeNetwork, activeGateway, obstructionInstances } = useProjectStore();
  const devices = useDeviceStore(state => state.devices);
  const { obstructionTypes } = useLookupsStore();
  const { getString } = useContext(LanguageContext);

  const createNoInstrumentsSelected = useCallback((): IGroupedDevices => {
    return {
      name: getString('blade_no_instruments'),
      deviceID: -1,
      deviceCategoryID: -3,
      detailUrl: null,
      imageUrl: null,
      image: null,
      description: null,
      children: [],
    };
  }, [getString]);

  useEffect(() => {
    const map: Record<number, IGroupedDevices> = {};
    devices.forEach((dev) => {
      dev.children.forEach((child) => {
        map[child.deviceID] = child;
      });
    });
    setDeviceMap(map);
  }, [devices]);

  useEffect(() => {
    const countInstances = (deviceInstances: IDeviceInstance[]): Record<number, IDeviceInfo> => {
      return deviceInstances.reduce((accurals: Record<number, IDeviceInfo>, instance: IDeviceInstance) => {
        const device = deviceMap[instance.deviceID];
        accurals[instance.deviceID] = accurals[instance.deviceID] || {
          count: 0,
          deviceID: instance.deviceID,
          deviceName: device.name,
          deviceCategoryID: device.deviceCategoryID,
          groupedDevice: device,
        };
        accurals[instance.deviceID].count += 1;
        return accurals;
      }, {});
    };

    let countedInstances: Record<number, IDeviceInfo> = [];
    if (activeNetwork && activeNetwork.deviceInstances && Object.keys(deviceMap).length > 0) {
      var devicesToCount = [...activeNetwork?.deviceInstances];
      if(activeGateway) {
        devicesToCount = [ activeGateway, ...devicesToCount];
      }   
      countedInstances = countInstances(devicesToCount);
    }

    const sorted = Object.values(countedInstances)
      .sort((a, b) => {
        // if (a.groupedDevice.deviceCategoryID === b.groupedDevice.deviceCategoryID){
        //   return a.groupedDevice.name.localeCompare(b.groupedDevice.name)
        // }
        return a.groupedDevice.deviceCategoryID - b.groupedDevice.deviceCategoryID; // TODO:STeve - add sort order to the group
      })
      .map((item) => ({
        ...item.groupedDevice,
        name: `(${item.count}) ${item.groupedDevice.name}`,
      }));

    let filtered = sorted.filter((item) => item.deviceCategoryID === DeviceCategory.Gateway || item.deviceCategoryID === DeviceCategory.Antenna);
    setGatewayDevices(filtered.length > 0 ? filtered : [createNoInstrumentsSelected()]);

    filtered = sorted.filter(
      (inst) => inst.deviceCategoryID !== DeviceCategory.Gateway && inst.deviceCategoryID !== DeviceCategory.Antenna && inst.deviceCategoryID !== DeviceCategory.Other,
    );
    setInstrumentDevices(filtered.length > 0 ? filtered : [createNoInstrumentsSelected()]);

    const other = sorted.filter((inst) => inst.deviceCategoryID === DeviceCategory.Other);
    if (activeNetwork && obstructionInstances.length > 0){
      const countObstrInstances = obstructionInstances.reduce((accruals: { [typeID: number]: number }, instance: IObstructionInstance) => {
        accruals[instance.obstructionTypeID] = (accruals[instance.obstructionTypeID] || 0) + 1
        return accruals;
      }, {});

      obstructionTypes.forEach((type) => {
        if (countObstrInstances[type.obstructionTypeID] > 0){
          other.push({
            name: `(${countObstrInstances[type.obstructionTypeID]}) ${type.obstructionTypeName}`,
            deviceID: 0,
            description: '',
            detailUrl: '',
            deviceCategoryID: DeviceCategory.Other,
            imageUrl: type.obstructionImageUrl,
            image: null,
            children: [],
          })
        }
      });
    }
    setOtherDevices(other.length > 0 ? other : [createNoInstrumentsSelected()]);
  }, [activeNetwork, deviceMap, createNoInstrumentsSelected, obstructionInstances, obstructionTypes, activeGateway]);

  const outermostContainer: IGroupedDevices[] = [
    {
      name: getString('blade_selected_gateway'),
      deviceID: -1,
      deviceCategoryID: -3,
      detailUrl: null,
      imageUrl: null,
      image: null,
      description: null,
      children: gatewayDevices,
    },
    {
      name: getString('blade_selected_insruments'),
      deviceID: -1,
      deviceCategoryID: -2,
      detailUrl: null,
      imageUrl: null,
      image: null,
      description: null,
      children: instrumentDevices,
    },
    {
      name: getString('blade_selected_other'),
      deviceID: -1,
      deviceCategoryID: -1,
      detailUrl: null,
      imageUrl: null,
      image: null,
      description: null,
      children: otherDevices,
    },
  ];

  return <Blade groupedDevices={outermostContainer} bgColor={theme.palette.common.white} level={0} plusMinusIconLevel={-1} />;
};

export default SelectedDevicesBlade;
